import _ from 'lodash';

export const ERROR_CODE = {
  ERROR_NONE: 0,
  REQUIRE_SCORES: 1,
  UNDER_REQUIREMENT: 2,
  DATA_NOT_READY: 99,
};

export const ERROR_CODE_TEXT = {
  [ERROR_CODE.ERROR_NONE]: null,
  [ERROR_CODE.REQUIRE_SCORES]: 'ยังกรอกคะแนนไม่ครบ',
  [ERROR_CODE.UNDER_REQUIREMENT]: 'มีคะแนนต่ำกว่าเกณฑ์',
  [ERROR_CODE.DATA_NOT_READY]: 'คำนวณคะแนนไม่ได้',
};

export const canCalculateScore = (examsList = []) => {
  const sumPercent = examsList.reduce((acc, value) => acc + (value.weightPercent || 0), 0);
  return sumPercent === 100;
};

export const getRelatedScores = (examIds = [], scores = []) =>
  scores.filter(s => examIds.includes(`${s.examId}`));

const sumCheckRule = (examIds = [], relatedScores = []) => {
  if (examIds.length !== relatedScores.length)
    return { error: 'คะแนนไม่ครบ', exams: examIds, code: ERROR_CODE.REQUIRE_SCORES };
  return null;
};

const oneOfCheckRule = (examIds = [], relatedScores = []) => {
  if (relatedScores.length === 0)
    return { error: 'คะแนนไม่ครบ', exams: examIds, code: ERROR_CODE.REQUIRE_SCORES };
  return null;
};

const sumCheckMinScoreOld = (exams = [], relatedScores = [], minScoreFix, minScorePercent) => {
  const relatedScoresKey = _.keyBy(relatedScores, 'examId');
  const minEachCheck = exams.reduce((prev, exam) => {
    const id = exam.id;
    const myScore = relatedScoresKey[id] || {};
    const result = checkMinScore(minScoreFix, minScorePercent, myScore.examScore, exam.fullScore, [
      id,
    ]);
    return [...prev, ...result];
  }, []);
  return minEachCheck;
};

const sumCheckMinScore = () => [];

const oneOfCheckMinScore = () => [];

export const sumSumRule = (relatedScores = []) => {
  return relatedScores.reduce(
    (acc, val) => ({ sum: acc.sum + val.examScore, exams: [...acc.exams, val.examId] }),
    { sum: 0, exams: [] }
  );
};

export const oneOfSumRule = (relatedScores = []) => {
  return relatedScores.reduce(
    (prev, val) => (prev.sum > val.examScore ? prev : { sum: val.examScore, exams: [val.examId] }),
    {
      sum: 0,
      exams: [],
    }
  );
};

export const totalScoreSumRule = (exams = []) =>
  exams.reduce((acc, exam) => acc + exam.fullScore, 0);

export const totalScoreOneOfRule = (exams = [], selectedId) => {
  const exam = exams.find(exam => exam.id == selectedId);
  if (exam) return exam.fullScore;
  return 0;
};

const getFunctionByRule = (rule = '') => {
  switch (rule) {
    case 'ONE_OF':
      return {
        check: oneOfCheckRule,
        sum: oneOfSumRule,
        total: totalScoreOneOfRule,
        minCheck: oneOfCheckMinScore,
      };
    case 'SUM':
    default:
      return {
        check: sumCheckRule,
        sum: sumSumRule,
        total: totalScoreSumRule,
        minCheck: sumCheckMinScore,
      };
  }
};

const checkMinScore = (minScoreFix, minScorePercent, score, totalScore, exams = []) => {
  const result = [];

  if (minScoreFix && score < minScoreFix)
    result.push({
      error: 'คะแนนต่ำกว่าเกณฑ์ขั้นต่ำ ' + minScoreFix,
      exams: exams,
      code: ERROR_CODE.UNDER_REQUIREMENT,
    });

  if (minScorePercent) {
    const totalPercent = (score / totalScore) * 100;
    if (totalPercent < minScorePercent)
      result.push({
        error: 'คะแนนต่ำกว่าเกณฑ์ขั้นต่ำ ' + minScorePercent + '%',
        exams: exams,
        code: ERROR_CODE.UNDER_REQUIREMENT,
      });
  }
  return result;
};

const calculateSingleRule = scores => rule => {
  // find exams in myScore
  // then choose logic
  // - SUM -> all exam should be in list
  // - ONE_OF -> either one should be in, then choose max of both
  // - MIN (should not occur)
  // then check for minScoreFix
  // - if less than, just add error information
  // then calculate base on rules ( SUM all, CHOOSE ONE )
  // get full score, then check for minScorePercent
  // - if less than, just add error information
  // then multiply with multiplier
  // then weight with weight percent
  // return each rule with sumScore, multipliedScore, weightedScore
  const result = { sumScore: 0, multipliedScore: 0, weightedScore: 0, error: [] };

  const ruleRule = rule.rule;
  const multiplier = rule.multiplier;
  const weightPercent = rule.weightPercent;
  const examIds = rule.exams.map(exam => exam.id);
  const { minScoreFix, minScorePercent } = rule;

  const relatedScores = getRelatedScores(examIds, scores);
  const { check, sum, total, minCheck } = getFunctionByRule(ruleRule);

  const checkResult = check(examIds, relatedScores);
  if (checkResult) result.error.push(checkResult);

  const minCheckResult = minCheck(rule.exams, relatedScores, minScoreFix, minScorePercent);
  result.error.push(...minCheckResult);

  const sumResult = sum(relatedScores);
  const sumValue = sumResult.sum;
  result.sumScore = sumValue;
  result.multipliedScore = sumValue * multiplier;
  result.weightedScore =
    sumResult.exams.length > 0
      ? (sumValue * multiplier * weightPercent) / sumResult.exams.length
      : 0;

  const minScoreErrors = checkMinScore(
    minScoreFix,
    minScorePercent,
    sumValue,
    total(rule.exams, sumResult.exams.length && sumResult.exams[0]),
    sumResult.exams
  );
  result.error.push(...minScoreErrors);

  return result;
};

export const calculate = (rules = [], scores = [], additionalRules = {}) => {
  if (!rules || rules.length === 0) return { code: ERROR_CODE.DATA_NOT_READY };
  if (!scores || scores.length === 0) return { code: ERROR_CODE.REQUIRE_SCORES };

  const globalMultiplier = additionalRules.multiplier || 1;

  const applicableRule = rules.filter(
    rule =>
      (rule.weightPercent > 0 && rule.multiplier > 0) ||
      rule.minScoreFix > 0 ||
      rule.minScorePercent > 0
  );
  // console.log(applicableRule);
  // check if weightPercent sum = 100 ?

  // for each rule
  const calculatedRules = applicableRule.map(calculateSingleRule(scores));

  // next calculate overall score by sum of all weightScore
  const result = calculatedRules.reduce(
    (acc, singleCalc) => {
      const newCode = singleCalc.error.reduce(
        (prevCode, value) => Math.max(value.code, prevCode),
        acc.code
      );
      return {
        sum: acc.sum + singleCalc.weightedScore * globalMultiplier,
        error: [...acc.error, singleCalc.error],
        rules: [...acc.rules, singleCalc],
        code: newCode,
      };
    },
    { sum: 0, error: [], rules: [], code: ERROR_CODE.ERROR_NONE }
  );

  return result;
};

export default { calculate, canCalculateScore, ERROR_CODE, ERROR_CODE_TEXT };
